import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/shared/Navigation';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Trophy, ArrowUp, ArrowDown, Palette } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { playingLevelsApi } from '@/lib/api';

interface PlayingLevel {
  id: number;
  name: string;
  description: string;
  color: string;
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
  members_count?: number;
}

interface PlayingLevelFormData {
  name: string;
  description: string;
  color: string;
  is_active: boolean;
  sort_order: number;
}

const defaultColors = [
  '#22c55e', // Green
  '#3b82f6', // Blue
  '#f59e0b', // Yellow
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#84cc16', // Lime
];

const AdminPlayingLevels = () => {
  const [playingLevels, setPlayingLevels] = useState<PlayingLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<PlayingLevel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PlayingLevelFormData>({
    name: '',
    description: '',
    color: '#22c55e',
    is_active: true,
    sort_order: 1
  });

  const fetchPlayingLevels = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await playingLevelsApi.getAll();
      
      let levels: PlayingLevel[] = [];
      if (Array.isArray(response)) {
        levels = response;
      } else if (response && Array.isArray(response.data)) {
        levels = response.data;
      } else {
        console.warn('Unexpected response format:', response);
        levels = [];
      }
      
      // Sort by sort_order
      levels.sort((a, b) => a.sort_order - b.sort_order);
      setPlayingLevels(levels);
    } catch (error) {
      console.error('Error fetching playing levels:', error);
      toast({
        title: "Error",
        description: "Failed to fetch playing levels",
        variant: "destructive"
      });
      setPlayingLevels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlayingLevels();
  }, [fetchPlayingLevels]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#22c55e',
      is_active: true,
      sort_order: playingLevels.length + 1
    });
  };

  const handleCreateLevel = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a level name",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const levelData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        color: formData.color,
        is_active: formData.is_active,
        sort_order: formData.sort_order
      };
      
      await playingLevelsApi.create(levelData);
      
      toast({
        title: "Success",
        description: "Playing level created successfully"
      });
      
      setIsCreateDialogOpen(false);
      resetForm();
      fetchPlayingLevels();
    } catch (error: any) {
      console.error('Error creating playing level:', error);
      
      let errorMsg = 'Failed to create playing level';
      if (error.response?.status === 401) {
        errorMsg = 'Unauthorized. Please login again.';
      } else if (error.response?.status === 403) {
        errorMsg = 'Forbidden. Admin access required.';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMsg = errors.join(', ');
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateLevel = async () => {
    if (!editingLevel || !formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a level name",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const levelData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        color: formData.color,
        is_active: formData.is_active,
        sort_order: formData.sort_order
      };
      
      await playingLevelsApi.update(editingLevel.id, levelData);
      
      toast({
        title: "Success",
        description: "Playing level updated successfully"
      });
      
      setIsEditDialogOpen(false);
      setEditingLevel(null);
      resetForm();
      fetchPlayingLevels();
    } catch (error: any) {
      console.error('Error updating playing level:', error);
      
      let errorMsg = 'Failed to update playing level';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMsg = errors.join(', ');
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLevel = async (id: number, name: string) => {
    try {
      await playingLevelsApi.delete(id);
      
      toast({
        title: "Success",
        description: `Playing level "${name}" deleted successfully`
      });
      
      fetchPlayingLevels();
    } catch (error: any) {
      console.error('Error deleting playing level:', error);
      
      let errorMsg = 'Failed to delete playing level';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      await playingLevelsApi.toggleStatus(id);
      
      toast({
        title: "Success",
        description: `Playing level ${currentStatus ? 'deactivated' : 'activated'} successfully`
      });
      
      fetchPlayingLevels();
    } catch (error: any) {
      console.error('Error toggling playing level status:', error);
      
      let errorMsg = 'Failed to update playing level status';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const handleReorder = async (levelId: number, direction: 'up' | 'down') => {
    const currentIndex = playingLevels.findIndex(level => level.id === levelId);
    if (currentIndex === -1) return;

    const newLevels = [...playingLevels];
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (swapIndex < 0 || swapIndex >= newLevels.length) return;

    // Swap sort_order values
    const temp = newLevels[currentIndex].sort_order;
    newLevels[currentIndex].sort_order = newLevels[swapIndex].sort_order;
    newLevels[swapIndex].sort_order = temp;

    try {
      await playingLevelsApi.reorder(
        newLevels.map(level => ({
          id: level.id,
          sort_order: level.sort_order
        }))
      );
      
      toast({
        title: "Success",
        description: "Playing levels reordered successfully"
      });
      
      fetchPlayingLevels();
    } catch (error: any) {
      console.error('Error reordering playing levels:', error);
      
      let errorMsg = 'Failed to reorder playing levels';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setFormData(prev => ({ ...prev, sort_order: playingLevels.length + 1 }));
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (level: PlayingLevel) => {
    setEditingLevel(level);
    setFormData({
      name: level.name,
      description: level.description,
      color: level.color,
      is_active: level.is_active,
      sort_order: level.sort_order
    });
    setIsEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Playing Levels</h1>
            <p className="text-gray-600 mt-2">Manage basketball playing skill levels for member registration</p>
          </div>
          
          <Button onClick={openCreateDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Add Playing Level
          </Button>
        </div>

        {playingLevels.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Playing Levels</h3>
              <p className="text-gray-600 mb-4">Get started by creating your first playing level.</p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Create Playing Level
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {playingLevels.map((level, index) => (
              <Card key={level.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full border-2 border-gray-200"
                        style={{ backgroundColor: level.color }}
                      />
                      <div>
                        <CardTitle className="text-lg flex items-center">
                          <Trophy className="w-5 h-5 mr-2 text-orange-500" />
                          {level.name}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Order: {level.sort_order}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={level.is_active ? "default" : "secondary"}>
                        {level.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {level.members_count !== undefined && (
                        <Badge variant="outline">
                          {level.members_count} members
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    {level.description || 'No description'}
                  </p>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReorder(level.id, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReorder(level.id, 'down')}
                        disabled={index === playingLevels.length - 1}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleStatus(level.id, level.is_active)}
                      >
                        {level.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(level)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Playing Level</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{level.name}"? This action cannot be undone.
                              {level.members_count && level.members_count > 0 && (
                                <span className="text-red-600 font-medium">
                                  <br />Warning: This level is currently used by {level.members_count} members.
                                </span>
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteLevel(level.id, level.name)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Playing Level</DialogTitle>
              <DialogDescription>
                Add a new playing level for member classification
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Professional"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this playing level..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="color">Color</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="color"
                    id="color"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="w-8 h-8 rounded border border-gray-300"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    placeholder="#22c55e"
                    className="flex-1"
                  />
                </div>
                <div className="flex space-x-1 mt-2">
                  {defaultColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="w-6 h-6 rounded border border-gray-300 hover:border-gray-400"
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  min="1"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleCreateLevel}
                disabled={isSubmitting}
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Create Level'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Playing Level</DialogTitle>
              <DialogDescription>
                Update the playing level information
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Professional"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this playing level..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-color">Color</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="color"
                    id="edit-color"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="w-8 h-8 rounded border border-gray-300"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    placeholder="#22c55e"
                    className="flex-1"
                  />
                </div>
                <div className="flex space-x-1 mt-2">
                  {defaultColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="w-6 h-6 rounded border border-gray-300 hover:border-gray-400"
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit-sort_order">Sort Order</Label>
                <Input
                  id="edit-sort_order"
                  type="number"
                  min="1"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="edit-is_active">Active</Label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsEditDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleUpdateLevel}
                disabled={isSubmitting}
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Update Level'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPlayingLevels; 